/**
 * The measurement surface on `window`. Nothing in the app reads it - it exists so a browser session
 * or a harness can ask what the feature is doing without adding logging to the code under test.
 */
import type { TDocumentModel } from './model/documentModel';
import type { renderStats } from './static/staticRenderCache';
import type { parseStats } from './model/wholeDocument';

export type TLargeDocumentApi = {
  /** True when THIS document is loaded in parts. False for a block or the mock sheet. */
  active: () => boolean;
  parts: () => number;
  activePart: () => number;
  elements: () => number;
  /** Elements the editor currently holds - `elements()` when the whole document is in it. */
  liveElements: () => number;
  /** The editor's document size. A typing measurement is only valid if this grew by the keys typed. */
  docSize: () => number;
  swapping: () => boolean;
  /** Whole-document parses. The claim is one per commit and none per keystroke. */
  parses: () => typeof parseStats;
  /** Per-part sizes: chars, elements, images, node views, table cells. Measurement only. */
  shape: () => ReturnType<TDocumentModel['partShape']> | null;
  /** Static-half rendering: how many element renders were started, finished, reused. */
  renders: () => typeof renderStats;
  /** Every action this feature took, with its numbers. See `trace.ts`. */
  log: () => readonly unknown[];
  clearLog: () => void;
  /** Adds the page's boxes to every entry from here on. Costs a forced layout per entry. */
  measure: (on: boolean) => void;
  /** Load the part holding an element id, the same way a click on the static half does. */
  goToId: (elementId: string) => boolean;
  /** Load a part by index. For sweeping every part in a harness; the app never addresses parts this way. */
  goToPart: (index: number) => boolean;
};

declare global {
  interface Window {
    __largeDocument?: TLargeDocumentApi;
  }
}

export const installDebugApi = (api: TLargeDocumentApi) => {
  if (typeof window !== 'undefined') window.__largeDocument = api;
};
